import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

export function getTreeItem(
  itemId: string,
  tree: ConfigTreeNode | null
): ConfigTreeNode | undefined {
  if (!tree) {
    return undefined;
  }

  // Virtual root doesn't need data
  if (itemId === 'root') {
    return undefined;
  }

  function findNodeInTree(node: ConfigTreeNode): ConfigTreeNode | undefined {
    if (node.id === itemId) {
      return node;
    }
    if (node.children) {
      for (const child of node.children) {
        const found = findNodeInTree(child);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  return findNodeInTree(tree);
}

export function getTreeChildrenNodes(itemId: string, tree: ConfigTreeNode | null): string[] {
  if (!tree) {
    return [];
  }

  // Virtual root has the main tree root as its child
  if (itemId === 'root') {
    return tree ? [tree.id] : [];
  }

  const node = getTreeItem(itemId, tree);
  if (node && node.children) {
    return node.children.map((child) => child.id);
  }

  return [];
}

/**
 * Ids of every node that has children, in depth-first order — i.e. exactly the set of
 * nodes that can be expanded.
 */
export function collectExpandableNodeIds(tree: ConfigTreeNode | null): string[] {
  if (!tree) {
    return [];
  }

  const ids: string[] = [];
  const walk = (node: ConfigTreeNode) => {
    if (!node.children || node.children.length === 0) {
      return;
    }
    ids.push(node.id);
    node.children.forEach(walk);
  };
  walk(tree);

  return ids;
}
